import { BaseWebComponentController, IListenerConfig } from '../../../framework/BaseWebComponentController';
import { PhasrMonitoredRulesGridWebComponent } from '../component/PhasrMonitoredRulesGridWebComponent';
import { PhasrMonitoredRulesGridRepository } from '../repository/PhasrMonitoredRulesGridRepository';
import { PhasrMonitoredRulesGridView } from '../view/PhasrMonitoredRulesGridView';
import { templatePhasrMonitoredRulesGrid } from '../view/template';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { PhasrMonitoredRulesGridEvents } from '../types';
import { PhasrApplyActionPanelWebComponent } from '../../../phasrApplyActionPanel';
import { translateFn } from '../../../utils/translator.helper';
import { Collections, PhasrApplyActionPanelEvents, PhasrApplyActionSchema } from '../../../phasrApplyActionPanel/types';
import { showToast } from '@nirvana/shared/common';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { BehavioralProfileTypes } from '../../../phasrRecommendations/phasrBehavioralProfilesComponent/types';
import { ERROR_CODE_NO_PROFILES_UPDATED } from '../../../types/common/phasrTypes';
import { PhasrPanelComponentTypes } from '../../../phasrSidePanel/types/types';
import { GridEvents } from '../../../grid/types/common';
import { SideMenuEvents } from '../../../ui/gzSideMenu/types';

export class PhasrMonitoredRulesGridController extends BaseWebComponentController {
  public component: PhasrMonitoredRulesGridWebComponent;
  public view: PhasrMonitoredRulesGridView;
  public viewConstructor: typeof PhasrMonitoredRulesGridView;
  public dataRepository: PhasrMonitoredRulesGridRepository;
  private restrictAccessDialog: GzDialogWebComponent;
  private behavioralProfileAutopilot = false;

  constructor(component: PhasrMonitoredRulesGridWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: PhasrMonitoredRulesGridView,
      viewTemplate: templatePhasrMonitoredRulesGrid,
      dataRepositoryConstructor: PhasrMonitoredRulesGridRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$grid,
        event: PhasrMonitoredRulesGridEvents.OnEditAccessFromGrid,
        callback: this.handleRestrictAccess,
      },
      {
        element: this.component,
        event: PhasrMonitoredRulesGridEvents.OnEditAccessFromDetailsPanel,
        callback: this.handleRestrictAccess
      },
      {
        element: this.view.dom.$grid,
        event: PhasrMonitoredRulesGridEvents.OnEditAccessFromSidePanel,
        callback: this.handleRestrictAccess
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.GRID_LOADED,
        callback: this.handleGridLoaded
      }
    ];
  }

  private async handleRestrictAccess(event: CustomEvent): Promise<void> {
    const detail = event.detail;
    await this.openEditAccessPanel(detail);
  }

  private handleGridLoaded(): void {
    if (this.component.openSideMenuForFirstItem) {
      const itemData = this.view.dom.$grid.controller?.view.dom.$grid.items[0];
      this.view.handlePhasrSidePanelOpen(PhasrPanelComponentTypes.MonitoredRulesDetails, document.createElement('div'), itemData);
      this.component.openSideMenuForFirstItem = false;
    }
  }

  private async reloadGridItems(): Promise<void> {
    const parentElement = this.component;
    const existingSidePanels = parentElement.shadowRoot?.querySelectorAll('.phasr-side-panel');
    existingSidePanels?.forEach((sidePanel) => sidePanel.dispatchEvent(
      new CustomEvent(SideMenuEvents.CLOSE, {
        bubbles: true,
        composed: true
      })
    ));
    await this.view.dom.$grid.controller?.handleGridItemsLoad();
  }

  private async closeEditAccessWindow(): Promise<void> {
    if (this.restrictAccessDialog !== null) {
      this.restrictAccessDialog.dismiss();
    }
    await this.reloadGridItems();
  }

  private async openEditAccessPanel(detail: PhasrApplyActionSchema): Promise<void> {
    const restrictAccessPanel = new PhasrApplyActionPanelWebComponent();

    const dialogConfig = {
      headline: translateFn('Edit access'),
      bodyContent: restrictAccessPanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: this.closeEditAccessWindow.bind(this),
    };

    this.restrictAccessDialog = GzDialogWebComponent.buildDialogElement(dialogConfig, document.getElementById('mainPanel') as HTMLElement);
    restrictAccessPanel.setGridData(detail);
    restrictAccessPanel.collection = Collections.RULES;
    restrictAccessPanel.addEventListener(
      PhasrApplyActionPanelEvents.CANCEL,
      this.closeEditAccessWindow.bind(this)
    );

    restrictAccessPanel.addEventListener(
      PhasrApplyActionPanelEvents.APPLY_ACTION,
      async (applyEvent: CustomEvent) => {
        try {
          const response = await this.dataRepository.updateUserBehaviouralProfiles(detail.id, detail.ruleId, detail.companyId, applyEvent.detail.changes);
          if (response.success) {
            try {
              analytics.trackPageInteraction({
                reference: 'phasr-monitored-rules-edit-access',
                asset: 'changes-applied',
              });
            } finally {
              showToast(translateFn('Successfully changed access.'), ToastSeverity.SUCCESS);
            }
          } else {
            const message =
              response.code && response.code === ERROR_CODE_NO_PROFILES_UPDATED
                ? translateFn('No profiles were updated.')
                : translateFn('Failed to change access.');
            showToast(message, ToastSeverity.INFORMATIVE);
          }
        } catch {
          showToast(translateFn('Failed to change access.'), ToastSeverity.ERROR);
        } finally {
          this.closeEditAccessWindow().then().catch();
        }
      },
      { once: true }
    );

    restrictAccessPanel.addEventListener(
      PhasrApplyActionPanelEvents.BEHAVIORAL_PROFILE_TYPE_CHANGED,
      (e: CustomEvent) => {
        this.behavioralProfileAutopilot = e.detail.selectedValue === BehavioralProfileTypes.AUTOPILOT;
      }
    )
    restrictAccessPanel.injectDependencies(this.dependencies);

    await restrictAccessPanel.start();
  }

  public async getDataForView(): Promise<{}> {
    if (this.dependencies.navigationParams) {
      this.component.openSideMenuForFirstItem = this.dependencies.navigationParams.openSideMenuForFirstItem;
    }

    return {};
  }

  public getCompanyIdFilterValue(): string {
    return this.view.getCompanyIdFilterValue();
  }
}
